import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MoreHorizontal } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { avText, avBg, avFg } from "../data/constants";
import { fetchConsumerUserDetail } from "../services/usersService";

// ── Grade Badge Component ───────────────────────────────────────────────────────
function GradeBadge({ grade }) {
  let bg = "bg-gray-100";
  let text = "text-gray-700";
  
  const g = (grade || "").toUpperCase();
  if (g === "A" || g === "EXCELLENT") {
    bg = "bg-[#DBF5F0]";
    text = "text-[#0B6172]";
  } else if (g === "B" || g === "GOOD") {
    bg = "bg-[#FFF3CD]";
    text = "text-[#F59E0B]";
  } else if (g === "C" || g === "CAUTION") {
    bg = "bg-[#FFE0DB]";
    text = "text-[#D32F2F]";
  } else if (g === "D" || g === "CONCERN" || g === "AVOID" || g === "F") {
    bg = "bg-[#FFDAD6]";
    text = "text-[#BA1A1A]";
  }

  return (
    <span className={`${bg} ${text} px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider`}>
      Grade <span className="font-black text-sm">{grade}</span>
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ZytamaUserDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: adminUser } = useAuth();

  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadUserDetail() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchConsumerUserDetail(id);
        setUserDetail(data);
      } catch (err) {
        setError(err.message || "Failed to load user details.");
      } finally {
        setLoading(false);
      }
    }
    loadUserDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <div className="w-8 h-8 border-4 border-[#DBF5F0] border-t-[#0B6172] rounded-full animate-spin" />
        <p className="text-[15px] text-[#0B6172] font-semibold">Loading user details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[20px] shadow-sm border border-[#0000001A] p-8 text-center flex flex-col items-center gap-4">
        <p className="font-bold text-red-500 text-[16px]">Failed to load user details</p>
        <p className="text-sm text-gray-500 max-w-md">{error}</p>
        <button 
          onClick={() => navigate(-1)}
          className="px-5 py-2 bg-[#0B6172] text-white rounded-lg text-sm font-semibold hover:bg-[#094f5e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const user = userDetail || {};
  const children = user.children || [];
  const scans = user.scans || { total: 0, items: [] };
  const userAvatar = user.avatar_url || "https://i.pravatar.cc/150?img=47";

  return (
    <div className="flex flex-col gap-6 pb-10">
      {/* Header */}
      <div className="bg-white rounded-[20px] shadow-sm border border-[#0000001A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 m-0">User Details Page</h1>
        </div>
        {/* <div 
          className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
          style={{ background: avBg(adminUser?.name), color: avFg(adminUser?.name), border: `2px solid ${avFg(adminUser?.name)}40` }}
        >
          {avText(adminUser?.name)}
        </div> */}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* ── Left Column ── */}
        <div className="flex-[2] flex flex-col gap-6">
          
          {/* Basic Information */}
          <div className="bg-[#FFFFFFB2] border border-[#0000001A] backdrop-blur-[20px] shadow-[0px_4px_32px_0px_#0000000A] rounded-[20px] p-6">
            <h2 className="text-lg font-bold text-[#0B1C30] mb-6">Basic information</h2>
            
            <div className="space-y-4">
              {user.name && (
                <div>
                  <label className="block text-sm font-medium text-[#00000099] mb-1.5">Full name</label>
                  <input 
                    type="text" 
                    value={user.name} 
                    disabled
                    className="w-full h-11 px-4 border border-[#00000059] rounded-[8px] bg-[#FFFFFF] text-[#0B1C30] placeholder-[#0B1C30] text-[15px] font-medium opacity-90 disabled:cursor-not-allowed disabled:text-[#0B1C30]"
                  />
                </div>
              )}

              {user.email && (
                <div>
                  <label className="block text-sm font-medium text-[#00000099] mb-1.5">Email address</label>
                  <input 
                    type="text" 
                    value={user.email} 
                    disabled
                    className="w-full h-11 px-4 border border-[#00000059] rounded-[8px] bg-[#FFFFFF] text-[#0B1C30] placeholder-[#0B1C30] text-[15px] font-medium opacity-90 disabled:cursor-not-allowed disabled:text-[#0B1C30]"
                  />
                </div>
              )}

              {user.phone && (
                <div className="flex gap-4">
                  {user.dial_code && (
                    <div className="flex-1 max-w-[140px]">
                      <label className="block text-sm font-medium text-[#00000099] mb-1.5">Dial code</label>
                      <div className="h-11 px-4 border border-[#00000059] rounded-[8px] bg-[#FFFFFF] flex items-center justify-between opacity-90 cursor-not-allowed">
                        <span className="text-[15px] font-medium text-[#0B1C30]">{user.dial_code}</span>
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1.5L6 6.5L11 1.5" stroke="#0B1C30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  <div className="flex-[2]">
                    <label className="block text-sm font-medium text-[#00000099] mb-1.5">Phone number</label>
                    <input 
                      type="text" 
                      value={user.phone} 
                      disabled
                      className="w-full h-11 px-4 border border-[#00000059] rounded-[8px] bg-[#FFFFFF] text-[#0B1C30] placeholder-[#0B1C30] text-[15px] font-medium opacity-90 disabled:cursor-not-allowed disabled:text-[#0B1C30]"
                    />
                  </div>
                </div>
              )}

              {user.city && (
                <div>
                  <label className="block text-sm font-medium text-[#00000099] mb-1.5">City</label>
                  <input 
                    type="text" 
                    value={user.city} 
                    disabled
                    className="w-full h-11 px-4 border border-[#00000059] rounded-[8px] bg-[#FFFFFF] text-[#0B1C30] placeholder-[#0B1C30] text-[15px] font-medium opacity-90 disabled:cursor-not-allowed disabled:text-[#0B1C30]"
                  />
                </div>
              )}

              {user.gender && (
                <div>
                  <label className="block text-sm font-medium text-[#00000099] mb-1.5">Gender</label>
                  <input 
                    type="text" 
                    value={user.gender} 
                    disabled
                    className="w-full h-11 px-4 border border-[#00000059] rounded-[8px] bg-[#FFFFFF] text-[#0B1C30] placeholder-[#0B1C30] text-[15px] font-medium opacity-90 disabled:cursor-not-allowed disabled:text-[#0B1C30]"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Total Scans */}
          <div className="bg-[#FFFFFFB2] border border-[#0000001A] backdrop-blur-[20px] shadow-[0px_4px_32px_0px_#0000000A] rounded-[20px] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#000000]">Total Scans <span className="text-[#0B6172]">({scans.total})</span></h2>
              <button className="text-[13px] font-bold text-[#0B6172] underline hover:no-underline">
                View more
              </button>
            </div>

            <div className="space-y-4">
              {scans.items && scans.items.length > 0 ? (
                scans.items.map(scan => (
                  <div key={scan.id} className="flex items-center justify-between p-3 border border-[#0000001A] rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                        <img 
                          src={scan.image_url || "https://images.unsplash.com/photo-1621939514649-280e2ee25f60?w=100&q=80"} 
                          alt={scan.product_name} 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <div>
                        <h3 className="text-[15px] font-bold text-[#081A3E] mb-1">{scan.product_name}</h3>
                        <p className="text-[13px] text-gray-500 font-semibold"><span className="text-[#0B6172] font-extrabold">{scan.score}</span> SCORE</p>
                      </div>
                    </div>
                    <GradeBadge grade={scan.grade} />
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No scans registered for this user.</p>
              )}
            </div>
          </div>

        </div>

        {/* ── Right Column ── */}
        <div className="flex-[1] flex flex-col gap-6">
          
          {/* Profile Card */}
          <div className="bg-[#FFFFFFB2] border border-[#0000001A] backdrop-blur-[20px] shadow-[0px_4px_32px_0px_#0000000A] rounded-[20px] p-8 flex flex-col items-center text-center">
            <div className="w-28 h-28 rounded-2xl mb-5 bg-[#0B6172]/10 text-[#0B6172] flex items-center justify-center text-5xl font-bold">
              {user.name ? user.name.charAt(0).toUpperCase() : '?'}
            </div>
            <h2 className="text-xl font-bold text-[#081A3E]">{user.name}</h2>
            <p className="text-[14px] text-gray-500 font-medium mt-1">{user.email}</p>
          </div>

          {/* Children Card */}
          <div className="bg-[#FFFFFFB2] border border-[#0000001A] shadow-[0px_4px_32px_0px_#0000000A] backdrop-blur-[20px] rounded-[20px] p-6">
            <h2 className="text-lg font-bold text-[#000000] mb-5">Children <span className="text-[#0B6172]">({children.length})</span></h2>
            
            <div className="space-y-4">
              {children.length > 0 ? (
                children.map((child, idx) => {
                  const stats1 = [];
                  if (child.age) stats1.push(child.age);
                  if (child.gender) stats1.push(child.gender);

                  const stats2 = [];
                  if (child.weight) stats2.push(child.weight);
                  if (child.height) stats2.push(child.height);

                  return (
                    <div key={child.id || idx} className="bg-[#FFFFFF] border border-[#0000001A] rounded-[10px] p-4 flex items-start justify-between relative">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl shrink-0 mt-0.5 bg-[#0B6172]/10 text-[#0B6172] flex items-center justify-center text-lg font-bold">
                          {child.name ? child.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <h3 className="text-[15px] font-bold text-[#0B1C30] mb-1">{child.name}</h3>
                          
                          {stats1.length > 0 && (
                            <p className="text-[11px] text-gray-500 font-medium mb-1">
                              {stats1.map((s, i) => (i === 0 ? `• ${s}` : ` • ${s}`)).join("")}
                            </p>
                          )}
                          
                          {stats2.length > 0 && (
                            <p className="text-[11px] text-gray-500 font-medium mb-3">
                              {stats2.map((s, i) => (i === 0 ? `• ${s}` : ` • ${s}`)).join("")}
                            </p>
                          )}

                          {child.allergies && child.allergies.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {child.allergies.map(allergy => (
                                <span key={allergy} className="bg-[#0B61721A] text-[#0B6172] border border-[#0B617233] px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide">
                                  {allergy}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <button className="text-gray-400 hover:text-gray-700">
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No children profiles registered.</p>
              )}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
